import { format } from 'date-fns';
import { omit } from 'lodash';
import { z } from 'zod';
import { License, LicenseTypeEnum } from '../../../../api';

export const getLicenseSchema = () =>
  z.array(
    z
      .object({
        id: z.string().uuid().optional(),
        type: z.string(),
        status: z.string(),
        number: z.string().optional().default(''),
        state: z.string().min(1, 'State is required'),
        file__id: z.string().uuid().optional(),
        file__file: z
          .union([
            z.string().url(), // valid URL for uploaded file
            z.string().length(0), // empty string
            z.null(), // null value
          ])
          .optional(), // field itself is optional
        expiration_date: z.union([
          z.string().length(0),
          z.null(),
          z.coerce.date().transform((val: Date) => {
            return new Date(val.valueOf() + val.getTimezoneOffset() * 60 * 1000);
          }),
        ]),
      })
      .superRefine((data, ctx) => {
        // Validate number if there's a file
        if (data.file__file && (!data.number || data.number.trim() === '')) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Required',
            path: ['number'],
          });
        }
        // Validate expiration date if there's a file
        if (data.file__file && !data.expiration_date) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Required',
            path: ['expiration_date'],
          });
        }
      })
  );

export const getProviderLicenses = (licenses: License[] = []) => {
  return licenses?.reduce(
    (acc, item) => {
      const sortedLicenses = [...(acc[item.type] ?? []), addFileField(item)].sort((a, b) =>
        a.state.localeCompare(b.state)
      );

      acc = {
        ...acc,
        [item.type]: sortedLicenses,
      };
      return acc;
    },
    {
      [LicenseTypeEnum.MEDICAL]: [],
      [LicenseTypeEnum.DEA]: [],
      [LicenseTypeEnum.CSR]: [],
    }
  );
};

export const addFileField = (item: { file: { file: string } }) => ({
  ...item,
  file__file: item.file?.file,
});

export const getProviderLicensesFromForm = function <T>(
  item: T & {
    file__file: string;
    expiration_date: Date;
  }
): T {
  return {
    ...omit(item, ['file__file', 'expiration_date']),
    expiration_date: item.expiration_date ? format(item.expiration_date, 'yyyy-MM-dd') : null,
    file: {
      file: item.file__file,
    },
  } as unknown as T;
};

// Add new function to sort certifications by type
export const sortCertificationsByType = <T extends { type: string }>(certifications: T[]) => {
  return [...certifications].sort((a, b) => a.type.localeCompare(b.type));
};
